import { type KeyboardEvent, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Icon, type IconName } from '../Icon.tsx';
import * as wording from '../wording.ts';
import { type BoardCard, type BoardColumn } from './model.ts';
import { CardActionsContext } from './PrimaryActionButton.tsx';
import type { ReorderStep } from './reorder.ts';

/**
 * What the board lends the card menu (US-026): the moves it may make, which
 * come from `transition()` so a menu move and a drop agree, and the steps up
 * and down the column a rankable card may take.
 */
export interface CardMenuHost {
  open(card: BoardCard): void;
  /** Every column, left to right, with the reason a move there is refused (`null` when allowed). */
  moves(card: BoardCard): readonly { readonly column: BoardColumn; readonly label: string; readonly refused: string | null }[];
  moveTo(card: BoardCard, column: BoardColumn): void;
  /** Per step, whether it moves the card; `null` outside the rankable columns. */
  steps(card: BoardCard): Readonly<Record<ReorderStep, boolean>> | null;
  reorder(card: BoardCard, step: ReorderStep): void;
  duplicate(card: BoardCard): void;
  /** Archives a content, or brings an archived one back. */
  archive(card: BoardCard): void;
}

type Entry =
  | { readonly kind: 'heading'; readonly key: string; readonly label: string }
  | { readonly kind: 'separator'; readonly key: string }
  | {
      readonly kind: 'item';
      readonly key: string;
      readonly label: string;
      readonly icon?: IconName;
      /** Why it is disabled; an item with a hint does nothing. */
      readonly hint?: string;
      readonly run: () => void;
    };

const STEP_LABEL: Readonly<Record<ReorderStep, string>> = {
  up: wording.CARD_MENU_MOVE_UP,
  down: wording.CARD_MENU_MOVE_DOWN,
  top: wording.CARD_MENU_TO_TOP,
  bottom: wording.CARD_MENU_TO_BOTTOM,
};

function entriesOf(card: BoardCard, host: CardMenuHost): Entry[] {
  const entries: Entry[] = [
    { kind: 'item', key: 'open', label: wording.CARD_MENU_OPEN, run: () => {
      host.open(card);
    } },
    { kind: 'separator', key: 'moves-sep' },
    { kind: 'heading', key: 'moves', label: wording.CARD_MENU_MOVE_TO },
  ];
  for (const { column, label, refused } of host.moves(card)) {
    const hint = column === card.column ? wording.CARD_MENU_HERE : refused;
    entries.push({
      kind: 'item',
      key: `move-${column}`,
      label,
      ...(hint === null ? {} : { hint }),
      run: () => {
        host.moveTo(card, column);
      },
    });
  }
  const steps = host.steps(card);
  if (steps !== null) {
    entries.push({ kind: 'separator', key: 'steps-sep' });
    for (const step of ['up', 'down', 'top', 'bottom'] as const) {
      const atEnd = step === 'up' || step === 'top' ? wording.CARD_MENU_AT_TOP : wording.CARD_MENU_AT_BOTTOM;
      entries.push({
        kind: 'item',
        key: `step-${step}`,
        label: STEP_LABEL[step],
        ...(steps[step] ? {} : { hint: atEnd }),
        run: () => {
          host.reorder(card, step);
        },
      });
    }
  }
  if (card.type === 'content') {
    const archived = card.content.archivedAt !== null;
    entries.push(
      { kind: 'separator', key: 'content-sep' },
      { kind: 'item', key: 'duplicate', label: wording.BOARD_DUPLICATE, icon: 'duplicate', run: () => {
        host.duplicate(card);
      } },
      {
        kind: 'item',
        key: 'archive',
        label: archived ? wording.BOARD_UNARCHIVE : wording.BOARD_ARCHIVE,
        icon: 'archive',
        run: () => {
          host.archive(card);
        },
      },
    );
  }
  return entries;
}

/**
 * The card's `kebab-horizontal` menu (US-026): Open, Move to… every column
 * (a refused one disabled, its reason underneath), the steps up and down a
 * rankable column, and Duplicate and Archive for a content. It is how a card
 * moves without a mouse or on a touch screen. Arrow keys, Home and End walk
 * it, Enter or Space picks, Esc and Tab close it; closing puts the focus
 * back on the card. Drawn in a portal, so a scrolling column cannot clip it.
 */
export function CardMenu({ card }: { readonly card: BoardCard }) {
  const actions = useContext(CardActionsContext);
  const host = actions?.menu;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [place, setPlace] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const entries = open && host !== undefined ? entriesOf(card, host) : [];
  const items = entries.filter((entry) => entry.kind === 'item');

  /** Closes the menu and gives the focus back to the card (unless it went somewhere on purpose). */
  const close = (refocus = true): void => {
    setOpen(false);
    setPlace(null);
    if (refocus) buttonRef.current?.closest<HTMLElement>('[data-card-id]')?.focus();
  };

  // Placed under the button, right edges aligned; above it when the window has no room below.
  useLayoutEffect(() => {
    if (!open) return;
    const button = buttonRef.current?.getBoundingClientRect();
    const menu = menuRef.current?.getBoundingClientRect();
    if (button === undefined || menu === undefined) return;
    const gap = 4;
    const below = button.bottom + gap;
    const top = below + menu.height > window.innerHeight - gap ? Math.max(gap, button.top - gap - menu.height) : below;
    const left = Math.min(Math.max(gap, button.right - menu.width), window.innerWidth - gap - menu.width);
    setPlace({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]')[active]?.focus();
  }, [open, active, place]);

  // A click elsewhere closes it where the click went; a scroll or resize would leave it floating.
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent): void => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) === true || buttonRef.current?.contains(target) === true) return;
      close(false);
    };
    const away = (event: Event): void => {
      if (menuRef.current?.contains(event.target as Node) === true) return;
      close(false);
    };
    document.addEventListener('pointerdown', outside, true);
    window.addEventListener('scroll', away, true);
    window.addEventListener('resize', away);
    return () => {
      document.removeEventListener('pointerdown', outside, true);
      window.removeEventListener('scroll', away, true);
      window.removeEventListener('resize', away);
    };
  }, [open]);

  if (host === undefined) return null;

  const pick = (index: number): void => {
    const item = items[index];
    if (item === undefined || item.hint !== undefined) return;
    close();
    item.run();
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    // The board's own keys wait until the menu is shut.
    event.stopPropagation();
    const count = items.length;
    switch (event.key) {
      case 'ArrowDown':
        setActive((index) => (index + 1) % count);
        break;
      case 'ArrowUp':
        setActive((index) => (index - 1 + count) % count);
        break;
      case 'Home':
        setActive(0);
        break;
      case 'End':
        setActive(count - 1);
        break;
      case 'Enter':
      case ' ':
        pick(active);
        break;
      case 'Escape':
      case 'Tab':
        close();
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  const toggle = (from: 'first' | 'last'): void => {
    if (open) {
      close();
      return;
    }
    setActive(from === 'first' ? 0 : entriesOf(card, host).filter((entry) => entry.kind === 'item').length - 1);
    setOpen(true);
  };

  let itemIndex = -1;
  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`button button--small button--icon board-card__menu${open ? ' board-card__menu--open' : ''}`}
        aria-label={wording.CARD_MENU}
        title={wording.CARD_MENU}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        draggable={false}
        onClick={() => {
          toggle('first');
        }}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
          event.preventDefault();
          event.stopPropagation();
          if (!open) toggle(event.key === 'ArrowDown' ? 'first' : 'last');
        }}
      >
        <Icon name="kebab-horizontal" />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            className="card-menu"
            role="menu"
            aria-label={wording.CARD_MENU}
            style={place === null ? { visibility: 'hidden', top: 0, left: 0 } : place}
            onKeyDown={onMenuKeyDown}
            // A portal still bubbles through React to the card: keep its click
            // (open the drawer) and drag from firing for the menu.
            onClick={(event) => {
              event.stopPropagation();
            }}
            onDragStart={(event) => {
              event.stopPropagation();
            }}
          >
            {entries.map((entry) => {
              if (entry.kind === 'separator') return <div key={entry.key} className="card-menu__separator" role="separator" />;
              if (entry.kind === 'heading') {
                return (
                  <div key={entry.key} className="card-menu__heading" role="presentation">
                    {entry.label}
                  </div>
                );
              }
              itemIndex += 1;
              const index = itemIndex;
              const hintId = `${menuId}-${entry.key}-hint`;
              return (
                <button
                  key={entry.key}
                  type="button"
                  role="menuitem"
                  className="card-menu__item"
                  tabIndex={index === active ? 0 : -1}
                  aria-disabled={entry.hint === undefined ? undefined : true}
                  aria-describedby={entry.hint === undefined ? undefined : hintId}
                  onMouseEnter={() => {
                    setActive(index);
                  }}
                  onClick={() => {
                    pick(index);
                  }}
                >
                  <span className="card-menu__label">
                    {entry.icon !== undefined && <Icon name={entry.icon} />}
                    {entry.label}
                  </span>
                  {entry.hint !== undefined && (
                    <span className="card-menu__hint" id={hintId}>
                      {entry.hint}
                    </span>
                  )}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}
